
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { ApiError } from '@empathyco/playboard-core-domain'
import type { KpisLegendItem } from '../../../composables/use-distribution-chart'

import type { KpisOptionComposable } from '../../../composables/use-kpis'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { useDistributionChart } from '../../../composables/use-distribution-chart'
import { KpiEnum, OriginsEnum } from '../../../enums'

import KpisLegend from '../../kpis-legend.component.vue'

interface RelatedPromptsDistributionChartData {
  date: Date
  queryCount: number
  clickCount: number
  add2cartCount: number
}

export default defineComponent({
  name: 'RelatedPromptsDistributionChart',
  components: {
    KpisLegend,
  },
  props: {
    selectedTab: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const baseClass = 'related-prompts-distribution-chart'

    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const messages = ctx.proxy.$epb.messages.insights.relatedPrompts.timelineGraph

    const loading = ref(false)

    const relatedPromptsResults = ref()

    const kpiOptions = computed((): KpisOptionComposable[] => [
      { id: KpiEnum.CLICKS, label: messages.clicks },
      { id: KpiEnum.ADD2CART, label: messages.add2carts },
    ])

    const kpisItems = computed((): KpisLegendItem[] => {
      return kpiOptions.value.map((option: KpisOptionComposable) => ({
        ...option,
        unfilled: true,
      }))
    })

    const getKpiValueFn = (
      kpi: KpiEnum,
    ): ((data: RelatedPromptsDistributionChartData) => number) => {
      return {
        [KpiEnum.CLICKS]: (data: RelatedPromptsDistributionChartData) => data.clickCount,
        [KpiEnum.ADD2CART]: (data: RelatedPromptsDistributionChartData) => data.add2cartCount,
      }[kpi]
    }

    const {
      timezone,
      selectedKpis,
      chartData: relatedPromptsDistributionChartData,
      isDailyData,
      context,
      fetchStatsReport,
      loadDistData,
    } = useDistributionChart<RelatedPromptsDistributionChartData>(
      kpiOptions.value,
      kpisItems.value,
      getKpiValueFn,
    )

    const fetchDistributionChartData = async function (
      origins = [
        'related_prompts:results',
        'related_prompts:no_results',
        'related_prompts:low_results',
        'related_prompts:none',
        'overview:none',
        'overview:results',
      ],
    ): Promise<StatsResponse> {
      const response: StatsResponse = await fetchStatsReport({
        id: isDailyData.value
          ? StatsReportsIdsEnum.HOURLY_ORIGINS
          : StatsReportsIdsEnum.DAILY_ORIGINS,
        params: {
          origins,
        },
      })

      return response.data.dist
    }

    const updateData = (): void => {
      if (relatedPromptsResults.value) {
        relatedPromptsDistributionChartData.value = relatedPromptsResults.value.map((item: any) => {
          return {
            date: item.date,
            queryCount: item.queryCount,
            clickCount: item.clickCount,
            add2cartCount: item.add2cartCount,
          }
        })
      }
    }

    const loadData = async (): Promise<void> => {
      loading.value = true
      try {
        const origins =
          props.selectedTab === OriginsEnum.NO_RESULTS
            ? ['related_prompts:no_results']
            : props.selectedTab === OriginsEnum.LOW_RESULTS
              ? ['related_prompts:low_results']
              : undefined

        relatedPromptsResults.value = await fetchDistributionChartData(origins)
      } catch (error) {
        ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error as ApiError))
      } finally {
        loading.value = false
      }
    }

    watch(
      () => [context.value.filters, props.selectedTab],
      async () => {
        await loadDistData()
        await loadData()
      },
      { immediate: true },
    )

    watch(
      () => relatedPromptsResults.value,
      () => {
        updateData()
      },
    )

    return {
      baseClass,
      timezone,
      loading,
      selectedKpis,
      isDailyData,
      kpisItems,
    }
  },
})
